/* Sending a member to KYCAID's hosted verification form.
 *
 * THE BUG THIS EXISTS TO PREVENT. The obvious version —
 *
 *     const data = await post('/kyc/start');
 *     window.open(data.formUrl, '_blank');
 *
 * — is blocked on every mobile browser and does absolutely nothing: no tab, no error, no
 * hint that anything happened. A popup is only permitted from the SYNCHRONOUS call stack of
 * a user gesture, and awaiting a network round-trip spends that gesture long before
 * window.open is reached. It is not a Safari quirk: iOS Safari, Chrome and Firefox on iOS
 * (all WebKit) and Chrome on Android all refuse it. Desktop Firefox is the lenient outlier,
 * which is exactly why this looked fine on a laptop and did nothing on an iPhone.
 *
 * So the tab is RESERVED on the click itself, while the gesture is still live, and pointed
 * at the form once the URL comes back. If even that was refused, we navigate the current tab
 * instead — KYCAID redirects back to /member-portal/kyc when they finish, so leaving the
 * portal costs nothing.
 */

export type KycTab = Window | null;

/* Call this FIRST, synchronously, in the click handler — before any await. That ordering is
 * the entire point; reserving the tab after the request is the bug. */
export const reserveKycTab = (): KycTab => {
  try {
    // deliberately NOT 'noopener': that makes window.open return null, and we need the
    // handle to point this tab at the form once we have the URL. The opener reference is
    // severed below instead, which gets the same protection without losing the handle.
    const tab = window.open('', '_blank');
    if (tab) {
      try { tab.opener = null; } catch { /* cross-origin once navigated; nothing to do */ }
    }
    return tab;
  } catch {
    return null;
  }
};

/* Hand the reserved tab over to the form. Returns where the member ended up, so the caller
 * can skip work that only makes sense if we are staying on this page. */
export const openKycForm = (tab: KycTab, formUrl: string): 'tab' | 'self' => {
  try {
    if (tab && !tab.closed) {
      // replace, not assign: the blank placeholder should not become a back-button stop
      tab.location.replace(formUrl);
      return 'tab';
    }
  } catch {
    /* the handle went unusable — fall through and use this tab */
  }
  window.location.assign(formUrl);
  return 'self';
};

/* The request failed, so there is no form to show — don't strand an empty tab in front of
 * the member with nothing in it. */
export const releaseKycTab = (tab: KycTab): void => {
  try { if (tab && !tab.closed) tab.close(); } catch { /* already gone */ }
};
